import { useState, useEffect } from "react";
import { AdminLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Progress } from "../../components/ui/progress";
import { toast } from "sonner";
import { 
  Lightbulb, AlertTriangle, Sparkles, Clock, CheckCircle, XCircle, 
  Loader2, MessageCircle, Send, TrendingUp, BarChart3
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
  verbesserung: { label: "Verbesserung", icon: Lightbulb, color: "bg-blue-500/10 text-blue-500" },
  problem: { label: "Problem", icon: AlertTriangle, color: "bg-red-500/10 text-red-500" },
  idee: { label: "Idee", icon: Sparkles, color: "bg-purple-500/10 text-purple-500" }
};

const STATUS_CONFIG = {
  neu: { label: "Neu", color: "bg-gray-500/10 text-gray-400", icon: Clock },
  in_bearbeitung: { label: "In Bearbeitung", color: "bg-yellow-500/10 text-yellow-500", icon: Loader2 },
  umgesetzt: { label: "Umgesetzt", color: "bg-green-500/10 text-green-500", icon: CheckCircle },
  abgelehnt: { label: "Abgelehnt", color: "bg-red-500/10 text-red-500", icon: XCircle }
};

const KVPManagement = () => {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("neu");
  const [selectedEntry, setSelectedEntry] = useState(null);
  
  // Dialogs
  const [detailDialog, setDetailDialog] = useState(false);
  
  // Form
  const [newStatus, setNewStatus] = useState("");
  const [adminComment, setAdminComment] = useState("");
  const [internalComment, setInternalComment] = useState("");

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      const [entriesRes, statsRes] = await Promise.all([
        axios.get(`${API}/kvp`, { params: activeTab !== "all" ? { status: activeTab } : {} }),
        axios.get(`${API}/kvp/stats/overview`)
      ]);
      setEntries(entriesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (entryId) => {
    try {
      const res = await axios.get(`${API}/kvp/${entryId}`);
      setSelectedEntry(res.data);
      setNewStatus(res.data.status);
      setAdminComment(res.data.admin_comment || "");
      setDetailDialog(true);
    } catch (error) {
      toast.error("Fehler beim Laden");
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedEntry) return;
    try {
      await axios.put(`${API}/kvp/${selectedEntry.id}/status`, null, {
        params: {
          status: newStatus,
          admin_comment: adminComment || undefined
        }
      });
      toast.success("Status aktualisiert");
      loadData();
      const res = await axios.get(`${API}/kvp/${selectedEntry.id}`);
      setSelectedEntry(res.data);
    } catch (error) {
      toast.error("Fehler");
    }
  };

  const handleInternalComment = async () => {
    if (!internalComment.trim() || !selectedEntry) return;
    try {
      await axios.post(`${API}/kvp/${selectedEntry.id}/comment`, null, {
        params: { content: internalComment, is_internal: true }
      });
      toast.success("Interner Kommentar hinzugefügt");
      setInternalComment("");
      const res = await axios.get(`${API}/kvp/${selectedEntry.id}`);
      setSelectedEntry(res.data);
    } catch (error) {
      toast.error("Fehler");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric"
    });
  };

  return (
    <AdminLayout title="KVP-Verwaltung">
      <div className="space-y-6" data-testid="kvp-management">
        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Lightbulb className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.total}</p>
                    <p className="text-xs text-muted-foreground">Gesamt</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.pending}</p>
                    <p className="text-xs text-muted-foreground">Offen</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.by_status?.umgesetzt || 0}</p>
                    <p className="text-xs text-muted-foreground">Umgesetzt</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                    <XCircle className="h-5 w-5 text-red-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.by_status?.abgelehnt || 0}</p>
                    <p className="text-xs text-muted-foreground">Abgelehnt</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.implementation_rate}%</p>
                    <p className="text-xs text-muted-foreground">Umsetzungsrate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Category Breakdown */}
        {stats && (
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Nach Kategorie</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                  const count = stats.by_category?.[key] || 0;
                  const Icon = config.icon;
                  return (
                    <div key={key} className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg ${config.color} flex items-center justify-center`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="font-bold">{count}</p>
                        <p className="text-xs text-muted-foreground">{config.label}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Entries Table */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle>Vorschläge verwalten</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">Alle</TabsTrigger>
                <TabsTrigger value="neu">Neu ({stats?.by_status?.neu || 0})</TabsTrigger>
                <TabsTrigger value="in_bearbeitung">In Bearbeitung</TabsTrigger>
                <TabsTrigger value="umgesetzt">Umgesetzt</TabsTrigger>
                <TabsTrigger value="abgelehnt">Abgelehnt</TabsTrigger>
              </TabsList>

              <TabsContent value={activeTab} className="mt-4">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : entries.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">Keine Einträge</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Titel</TableHead>
                        <TableHead>Kategorie</TableHead>
                        <TableHead>Autor</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Datum</TableHead>
                        <TableHead className="text-right">Aktion</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.filter(e => activeTab === "all" || e.status === activeTab).map(entry => {
                        const catConfig = CATEGORY_CONFIG[entry.category];
                        const statusConfig = STATUS_CONFIG[entry.status];
                        return (
                          <TableRow key={entry.id}>
                            <TableCell className="font-medium">{entry.title}</TableCell>
                            <TableCell>
                              <Badge className={catConfig?.color}>{catConfig?.label}</Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{entry.author?.name}</TableCell>
                            <TableCell>
                              <Badge className={statusConfig?.color}>{statusConfig?.label}</Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{formatDate(entry.created_at)}</TableCell>
                            <TableCell className="text-right">
                              <Button variant="outline" size="sm" onClick={() => openDetail(entry.id)}>
                                Bearbeiten
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
            {selectedEntry && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={CATEGORY_CONFIG[selectedEntry.category]?.color}>
                      {CATEGORY_CONFIG[selectedEntry.category]?.label}
                    </Badge>
                    {selectedEntry.area && <Badge variant="outline">{selectedEntry.area}</Badge>}
                  </div>
                  <DialogTitle className="text-xl mt-2">{selectedEntry.title}</DialogTitle>
                  <div className="text-sm text-muted-foreground">
                    Von {selectedEntry.author?.name} am {formatDate(selectedEntry.created_at)}
                  </div>
                </DialogHeader>

                <div className="py-4 border-b border-border">
                  <p className="whitespace-pre-wrap">{selectedEntry.description}</p>
                </div>

                {/* Status Update */}
                <div className="py-4 border-b border-border space-y-4">
                  <h4 className="font-medium">Status aktualisieren</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm">Status</label>
                      <Select value={newStatus} onValueChange={setNewStatus}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                            <SelectItem key={key} value={key}>{config.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm">Admin-Antwort (öffentlich)</label>
                    <Textarea
                      value={adminComment}
                      onChange={(e) => setAdminComment(e.target.value)}
                      placeholder="Antwort für den Teilnehmer..."
                      rows={3}
                    />
                  </div>
                  <Button onClick={handleStatusUpdate}>Status speichern</Button>
                </div>

                {/* Comments */}
                <div className="space-y-4 py-4">
                  <h4 className="font-medium">Kommentare ({selectedEntry.comments?.length || 0})</h4>
                  
                  {selectedEntry.comments?.map(comment => (
                    <div key={comment.id} className={`p-3 rounded-lg ${comment.is_internal ? "bg-yellow-500/10 border border-yellow-500/30" : "bg-secondary/30"}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{comment.author?.name}</span>
                          {comment.is_internal && <Badge variant="outline" className="text-xs">Intern</Badge>}
                        </div>
                        <span className="text-xs text-muted-foreground">{formatDate(comment.created_at)}</span>
                      </div>
                      <p className="text-sm">{comment.content}</p>
                    </div>
                  ))}

                  {/* Internal Comment */}
                  <div className="flex gap-2">
                    <Textarea
                      value={internalComment}
                      onChange={(e) => setInternalComment(e.target.value)}
                      placeholder="Internen Kommentar hinzufügen (nur für Admins sichtbar)..."
                      rows={2}
                      className="flex-1"
                    />
                    <Button onClick={handleInternalComment} disabled={!internalComment.trim()}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default KVPManagement;
